import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ServicesManager } from '@ohif/core';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function getSessionIdFromCookieOrStorage() {
  const cookieMatch = /(?:^|; )session_id=([^;]*)/.exec(document.cookie);
  const cookieSessionId = cookieMatch?.[1] ? decodeURIComponent(cookieMatch[1]).trim() : '';

  if (cookieSessionId && cookieSessionId !== 'undefined' && cookieSessionId !== 'null') {
    return cookieSessionId;
  }

  const storageSessionId = localStorage.getItem('session_id');
  if (storageSessionId && storageSessionId !== 'undefined' && storageSessionId !== 'null') {
    return storageSessionId;
  }

  return null;
}

const SupportDialog = ({ isOpen, onClose, onSubmit, uiNotificationService }) => {
  const [label, setLabel] = useState('');
  const [reasons, setReasons] = useState({
    savedConfigurations: false,
    measurements: false,
    segmentations: false,
  });
  const [project, setProject] = useState(null);
  const [patient, setPatient] = useState(null);

  const query = useQuery();

  // Initialize project and patient from sessionStorage
  useEffect(() => {
    const initializeData = () => {
      const data = JSON.parse(sessionStorage.getItem('_ctx_key'));
      if (data && Array.isArray(data)) {
        setPatient(data[0]); // first element = patient_id
        setProject(data[1]); // second element = project_id
      }
    };
    initializeData();
  }, [query]);

  const checkboxes = [
    { key: 'savedConfigurations', label: 'Saved Configurations' },
    { key: 'measurements', label: 'Measurements' },
    { key: 'segmentations', label: 'Segmentations' },
  ];

  const handleCheckboxChange = key => {
    setReasons(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const API_URL_ENV = process.env.API_URL;

  const handleSubmit = async e => {
    e.preventDefault();

    const viewer_token = sessionStorage.getItem('session_viewer');

    try {
      // Get labels of selected checkboxes
      const selectedReasons = checkboxes.filter(c => reasons[c.key]).map(c => c.label);

      const payload = {
        text: label, // textarea content
        options: selectedReasons, // selected checkbox labels
      };

      const session_id = getSessionIdFromCookieOrStorage();
      if (session_id) {
        myHeaders.append('x-api-key', session_id);
      }

      const response = await fetch(`${API_URL_ENV}/requests/ct-viewer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${viewer_token}`,
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${data.detail || data.message}`);
      }

      console.log('API response:', data);

      // Reset state
      setLabel('');
      setReasons({
        savedConfigurations: false,
        measurements: false,
        segmentations: false,
      });

      uiNotificationService.show({
        title: 'Notification',
        message: 'The message was sent successfully to our support team.',
        type: 'success',
        duration: 5000,
      });

      onSubmit?.(data);
      onClose();
    } catch (error) {
      console.error(error);
      uiNotificationService.show({
        title: 'Notification',
        message: error.message || 'An error occurred while sending the message.',
        type: 'error',
        autoClose: false,
      });
    }
  };

  if (!isOpen) return null;

  // Enable submit only if textarea has text and at least one checkbox selected
  const valid = label.trim() !== '' && Object.values(reasons).some(Boolean);

  return (
    <div
      id="support-dialog-ct"
      className="z-100 fixed inset-0 flex items-center justify-center bg-black/50"
    >
      <div
        className="w-[500px] max-w-full rounded-lg p-6 shadow-lg"
        style={{ backgroundColor: 'rgb(251, 249, 249)' }}
      >
        {/* Title */}
        <h1
          className="mb-4 text-2xl font-bold"
          style={{ color: '#AA91C2' }}
        >
          Something wrong with CT Viewer? Contact us
        </h1>

        <form onSubmit={handleSubmit}>
          {/* Checkboxes */}
          <div className="mb-6">
            <span className="mb-2 block text-lg font-medium text-black">Select Reasons:</span>
            <div className="flex flex-wrap gap-4">
              {checkboxes.map(c => (
                <label
                  key={c.key}
                  className="flex items-center space-x-2 text-black"
                >
                  <input
                    type="checkbox"
                    checked={reasons[c.key]}
                    onChange={() => handleCheckboxChange(c.key)}
                    className="h-5 w-5 rounded border border-gray-300 bg-white accent-[#AA91C2]"
                  />
                  <span>{c.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Textarea */}
          <div className="mb-4">
            <textarea
              className="h-28 w-full resize-none rounded border border-gray-300 p-3 text-black focus:outline-none focus:ring-2 focus:ring-[#AA91C2]"
              value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder="Enter details about the issue..."
              autoFocus
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              className="px-4 py-2 font-semibold text-black hover:text-[#AA91C2]"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!valid}
              className={`rounded px-4 py-2 font-semibold text-black ${
                !valid
                  ? 'cursor-not-allowed bg-gray-400 text-white'
                  : 'bg-[#AA91C2] hover:bg-[#997DBA]'
              }`}
            >
              Send Message
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SupportDialog;
